<script setup lang="ts">
/**
 * 換發新證（批108）——我方後台與代理商共用。
 *
 * 從既有的證換一張，可同時改套裝／數量配額／期限。以前要改這些只能走申請
 * 三段式（建草稿 → 確認送出 → 核發）。
 *
 * ## 批114：數量一律在「數量配額」裡填
 *
 * 這張表單原本另外有兩個獨立的數字欄。那兩個數字在每個產品的憑證設計裡本來就是配額鍵，
 * `QuotaEditor` 也一直畫得出來——同一個數字有兩個輸入框，而獨立欄還會靜默蓋掉配額裡填的
 * 值。批114 把它們拔掉，送出時只送 `quotas` 一包（後端仍相容收得下舊的一級欄）。
 * 必填與「可不可以無上限」由憑證設計的旗標決定，`QuotaEditor` 的 `update:valid` 會回報，
 * 沒填齊就不讓送出（後端那一關是 422 `quotas_invalid`）。
 *
 * ## 兩件要在按下去之前講清楚的事
 *
 * 1. **換發會作廢目前那張證**。技術支援為了救援換一張短期證時，等於把客戶原本的長期
 *    授權作廢了——短期證一到期，站台直接轉唯讀。所以期限比原證短的時候要紅字二次確認。
 * 2. **內容偏離套裝模板會被政策閘擋**（403 `activation_requires_approval`），
 *    那不是錯誤而是規則：要改成非標準內容就得走授權申請流程。錯誤訊息要指路。
 *
 * ## 批110：模組／額度／行為參數**可以在這裡逐項改了**（工單 #5495）
 *
 * 批108 的版本刻意不開，理由是「客製內容要走申請流程」。但那條規則的實際後果是
 * **只有 superadmin 動得了一張證的額度**（客製內容一律升到最嚴政策），而那是營運日常。
 * 批110 把客製內容降到「營運主管」之後，這裡就該開——同一件事不必再開一張申請單。
 *
 * 規則沒有變鬆的部分：
 * - 展開「自訂內容」才會送這三個欄位；**收合時完全不送**（沿用原證，與批108 行為逐位元相同）。
 * - 送了就是偏離模板 → 政策閘把它升到 `manager`，`admin` 與代理商照樣 403 並指路申請流程。
 * - **換套裝時仍然由伺服器端整包展開**（不信前端送回的展開值，否則模板只是下拉的裝飾）。
 *
 * ★ 代理商 realm **看不到這一區**（後端也顯式拒收 `policy`／`not_before`）。
 *
 * ## 批113：換套裝要把模板值**帶進畫面**（工單 #5523）
 *
 * 批110 開了「自訂內容」之後出現一個新的死結：換套裝時三個編輯器裡放的還是**舊證**的
 * 模組／額度／行為參數，按下換發就把舊內容原樣送回去 → 偏離新套裝模板 → 403。操作者
 * 只是「換個套裝」，畫面上卻完全看不出自己送了什麼。所以這裡多一個 `watch(plan)`，
 * 把 `api/admin.py::reissue_license_flow`（:1581-1607）的展開規則**在前端鏡像一次**：
 * 少一鍵就是一個 403，`ReissueModal.spec.ts` 前四支測試就是這面鏡子的釘子。
 *
 * 另一條出路：403 時給一顆「改建授權草稿」——換發本來就沒有「降級成申請」的路，
 * 而客戶頁那支草稿的主機下拉只列**已連結**的主機（`deployment_links`），走過去反而
 * 選不到這台，核發時 `prior` 是 None → 舊證不會被取代、變成兩張各自有效的證。
 */
import { computed, nextTick, ref, watch } from 'vue'

import ModulePicker from '@/components/ModulePicker.vue'
import PolicyEditor from '@/components/PolicyEditor.vue'
import QuotaEditor from '@/components/QuotaEditor.vue'
import TermField from '@/components/TermField.vue'
import UiButton from '@/components/UiButton.vue'
import UiModal from '@/components/UiModal.vue'
import { ApiError } from '@/api/client'
import * as ls from '@/api/ls'
import type { ProductSpec } from '@/api/ls'
import * as reseller from '@/api/reseller'
import { knownErrorText } from '@/lib/apiErrors'
import { isInternalPlan } from '@/lib/products'
import { quotaRequired } from '@/lib/quotas'
import { seedQuotas } from '@/lib/seedQuotas'
import { MINUTES_PER_DAY, formatTerm, hasTerm, splitMinutes, totalMinutes } from '@/lib/terms'
import { useDialogsStore } from '@/stores/dialogs'

const props = defineProps<{
  open: boolean
  /** 基準證（任何狀態都可以——判準是機器，不是證的狀態）。 */
  license: {
    license_id: string
    /** 批113：403 時「改建授權草稿」要指名客戶（我方清單與代理商清單的列都帶）。 */
    customer_id: string
    /** 批113：草稿的 `request_json.product`；代理商清單的型別是選填，這裡跟著選填。 */
    product?: string
    plan: string | null
    expires_at: string | null
    term_days?: number | null
    term_minutes?: number
    status: string
    /** 批113：草稿要綁**同一台機器**，否則核發時舊證不會被標成「已被取代」。 */
    host_instance_id?: string | null
    machine_fingerprint?: Record<string, string> | null
    deployment_mode?: string
    /** 批110：現行的模組／額度／行為參數——「自訂內容」展開時的起手值。 */
    modules?: Record<string, string> | null
    /**
     * ★ 批114：這裡刻意**只宣告 `quotas`**。基準證上那些相容輸出的一級數字欄由
     * `lib/seedQuotas.ts` 讀（它吃 `unknown`，鍵名集中在那一支）——在這個元件裡再宣告
     * 一次，就等於把某個產品的欄位名寫回元件層。
     */
    quotas?: Record<string, number | null> | null
    policy?: Record<string, unknown> | null
    not_before?: string | null
  } | null
  /** 該產品的憑證設計（套裝清單、配額與行為參數的鍵定義）。 */
  spec: ProductSpec | null
  /** 代理商 realm 走自己那支端點（同一個後端本體）。 */
  isReseller?: boolean
  /**
   * 批155：可以選內部套裝／勾內部模組（我方頁面傳 `auth.isManagerOrAbove`）。缺省＝不可以：
   * 內部套裝不列（基準證本來就是它時照列但停用，免得下拉空白），內部模組見 `ModulePicker`。
   */
  allowInternal?: boolean
}>()
const emit = defineEmits<{ close: []; done: [payload: { license_id: string }] }>()

const plan = ref<string | null>(null)
/** 期限（總分鐘）；`null`＝沿用基準證。 */
const termTotal = ref<number | null>(null)
const expiryMode = ref<'on_fetch' | 'keep' | 'at'>('on_fetch')
const expiresAt = ref('')
/** 批110：延後生效（`datetime-local`；空＝立刻生效）。 */
const notBefore = ref('')
const note = ref('')
/** 批110：展開才會送模組／額度／行為參數（收合＝沿用原證，與批108 行為相同）。 */
const custom = ref(false)
const modules = ref<Record<string, string>>({})
const quotas = ref<Record<string, number | null>>({})
const policy = ref<Record<string, unknown>>({})
/**
 * 批114：`QuotaEditor` 回報的「這包配額填得完整嗎」（必填鍵有沒有值）。
 *
 * 收合「自訂內容」時編輯器根本沒掛上、也不會送 `quotas` → 一律視為通過（回到 true），
 * 否則展開填錯一次、收合之後按鈕就永遠是灰的。
 */
const quotaValid = ref(true)
/** 三個編輯器都是「掛載時 initFrom」，所以每次展開要換 key 重掛。 */
const customKey = ref(0)
const busy = ref(false)
const error = ref('')
/** 批113：政策閘 403 之後才亮的「改建授權草稿」（代理商不給——他們走自己的申請頁）。 */
const canDraft = ref(false)
const draftBusy = ref(false)
const dialogs = useDialogsStore()
/**
 * 批113：`watch(plan)` 的抑制旗標。
 *
 * 開窗時 `plan` 會從上一次的值（或 `null`）被設成基準證的套裝名——那是「帶入」不是
 * 「操作者換了套裝」，但兩者在 watcher 眼裡長得一模一樣（**不能只靠 `next !== prev` 判**）。
 * 沒有這個旗標的話，用舊證內容開窗會被當成換套裝、額度當場被模板值蓋掉。
 */
let seeding = false

/**
 * 只列可販售的主套裝（加購模板不在這裡換）。
 * 批155：內部套裝只有 `allowInternal` 時才列；基準證本來就是內部套裝時照列（選項停用，見 template）。
 */
const plans = computed(() =>
  (props.spec?.plans ?? []).filter(
    (p) => p.kind !== 'addon' && (!isInternalPlan(p) || props.allowInternal || p.key === props.license?.plan),
  ),
)
/** 這個套裝這個身分選不了（內部套裝、而不是營運主管以上）。 */
function planLocked(p: { internal?: boolean }): boolean {
  return isInternalPlan(p) && !props.allowInternal
}

type BaseLicense = NonNullable<typeof props.license>

/**
 * 整組回到基準證的內容。
 *
 * 對應後端「沒換套裝」那條路：`reissue_license_flow` 只跑 `issue_svc._carry_over(base)`，
 * 不重新展開模板——所以畫面上看到的就該是原證的值。
 */
function seedFromLicense(lic: BaseLicense) {
  termTotal.value = totalMinutes(lic.term_days, lic.term_minutes) || null
  modules.value = { ...(lic.modules ?? {}) }
  // 批114：基準證可能是**舊版簽的**——數量還在一級欄、`quotas` 裡沒有那個鍵。
  // `seedQuotas` 在鍵缺席時才把它併進來，客戶明確選的「不限」（值 null）不會被蓋掉。
  quotas.value = seedQuotas(props.spec, lic) ?? {}
  policy.value = { ...(lic.policy ?? {}) }
}

/**
 * 換套裝時，模板 `quotas` 沒宣告的**必填鍵**沿用基準證的值（批114 的通用化）。
 *
 * 批113 是寫死一個鍵名在做這件事：企業級那種「這個數字自己填」的套裝，模板 `quotas` 裡
 * 根本沒有那個鍵，整包覆蓋會把原證的數字丟掉 → 簽發時 422。判準是**鍵存在性**不是
 * `!= null`（值 null＝明確的「不限」，不該被舊數字蓋回去），現在改成對憑證設計上
 * 標為「必填」的每個鍵都做，不再認得任何一個具體鍵名。
 */
function carryRequiredQuotas(
  tpl: Record<string, number | null>,
  lic: BaseLicense,
): Record<string, number | null> {
  const base = seedQuotas(props.spec, lic) ?? {}
  const out = { ...tpl }
  for (const def of props.spec?.quotas ?? []) {
    if (!quotaRequired(def) || def.key in out) continue
    const v = base[def.key]
    if (typeof v === 'number') out[def.key] = v
  }
  return out
}

watch(
  () => props.open,
  (open) => {
    if (!open || !props.license) return
    const lic = props.license
    seeding = true
    plan.value = lic.plan
    seedFromLicense(lic)
    expiryMode.value = 'on_fetch'
    expiresAt.value = ''
    notBefore.value = ''
    note.value = ''
    error.value = ''
    canDraft.value = false
    custom.value = false
    customKey.value += 1
    // `watch(plan)` 是 pre-flush，會在這次 tick 的 render 之前跑完；`nextTick` 排在
    // 它後面，所以旗標一定是「帶入期間為 true、操作者動手時已是 false」。
    void nextTick(() => {
      seeding = false
    })
  },
)

/**
 * 換套裝＝賣的是另一份商品，內容整包以新模板為準。
 *
 * ★★ 這段是 `api/admin.py::reissue_license_flow`（:1581-1607）的鏡像，**逐鍵對稱**：
 *   - 判準與後端的 `switched_plan = plan_key != base.plan` 一致——選「客製（null）」或
 *     **選回原證的套裝**都不算換套裝（後端那時走 `_carry_over`，畫面就該回到原證）。
 *   - 模板沒宣告的**必填配額鍵**沿用基準證的值（`carryRequiredQuotas`，判準是鍵存在性）。
 *   - 期限用 `hasTerm`（＝後端 `terms.has_term`）而不是 `if (p.term_days)`：分鐘級模板的
 *     `term_days` 是 **0**，truthy 判斷會把「2 小時體驗」看成沒有期限。
 *
 * 展開狀態**刻意不動**：自動展開會改變 submit 的語意（送出三欄＝偏離模板），自動收合
 * 則讓操作者剛改好的內容無聲消失。
 */
watch(plan, (next) => {
  if (seeding || !props.open || !props.license) return
  const lic = props.license
  const p = next ? plans.value.find((x) => x.key === next) : undefined
  if (!p || next === lic.plan) {
    seedFromLicense(lic)
  } else {
    // 模板沒帶 `quotas`（整包 null）也要跑一次：那種套裝的必填數量同樣是「自己填」，
    // 起手值該是基準證的值而不是空的。
    quotas.value = carryRequiredQuotas(p.quotas ? { ...p.quotas } : {}, lic)
    modules.value = { ...p.modules }
    policy.value = { ...(p.policies ?? {}) }
    termTotal.value = hasTerm(p.term_days, p.term_minutes)
      ? totalMinutes(p.term_days, p.term_minutes)
      : totalMinutes(lic.term_days, lic.term_minutes) || null
  }
  // 三個編輯器都是「掛載時 initFrom」——不換 key 的話帶入的新值只存在於 ref 裡，
  // 畫面上還是舊值（而送出去的是新值，兩邊對不上是最難查的那種 bug）。
  customKey.value += 1
})

/**
 * 展開／收合「自訂內容」（批114）。
 *
 * 收合時把 `quotaValid` 收回 true：那一刻起這三欄一個都不送，配額填不填得齊與這次換發
 * 無關；不重設的話，展開時留下的 false 會讓「換發」永遠是灰的。
 */
function toggleCustom() {
  custom.value = !custom.value
  if (!custom.value) quotaValid.value = true
}

/** 配額沒填齊＝擋住送出（只在展開時成立）。 */
const quotaBlocked = computed(() => custom.value && !quotaValid.value)

/** 目前選的套裝標籤（提示行用）。 */
const planLabel = computed(() => plans.value.find((p) => p.key === plan.value)?.label ?? '')

/**
 * 「自訂內容」展開時的帶入提示——只有在**選的套裝不是原證那個**時才有話要說
 * （相同時畫面上的值本來就是原證的值，多一行提示反而像出了什麼事）。
 */
const seededHint = computed(() => {
  if (!custom.value || !props.license || plan.value === props.license.plan) return ''
  if (plan.value === null) return '已回到原證的模組／額度／行為參數（客製＝沿用原內容）。'
  return `已帶入「${planLabel.value}」的模板預設值，未逐項改動則與伺服器端展開的內容一致。`
})

/** 原證的啟用時間（`YYYY-MM-DD HH:mm`，本地時區）；沒有就空字串。 */
const baseNotBefore = computed(() => {
  const iso = props.license?.not_before
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const p2 = (n: number) => String(n).padStart(2, '0')
  return (
    `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())} ` +
    `${p2(d.getHours())}:${p2(d.getMinutes())}`
  )
})

/** 新期限比原證剩餘時間短 → 出紅字（救援換短期證最容易忘的那件事）。 */
const shortWarning = computed(() => {
  if (!props.license || expiryMode.value !== 'on_fetch') return ''
  const total = termTotal.value ?? 0
  if (total <= 0 || total >= MINUTES_PER_DAY) return ''
  return (
    `這會作廢目前那張授權，換成一張「${formatTerm(...splitMinutes(total))}」的短期授權。` +
    '到期後站台會轉唯讀，請記得在到期前補發正式授權。'
  )
})

async function submit() {
  if (!props.license) return
  busy.value = true
  error.value = ''
  canDraft.value = false
  try {
    const body: Record<string, unknown> = {
      plan: plan.value,
      expiry_mode: expiryMode.value,
      note: note.value.trim() || null,
    }
    if (termTotal.value != null) {
      const [d, m] = splitMinutes(termTotal.value)
      body.term_days = d
      body.term_minutes = m
    }
    if (expiryMode.value === 'at') body.expires_at = new Date(expiresAt.value).toISOString()
    if (notBefore.value) body.not_before = new Date(notBefore.value).toISOString()
    // ★ 收合時**一個欄位都不送**：沒展開就是「沿用原證」，送了空 dict 會被後端當成
    //   「把模組清空」——那是靜默關掉客戶所有功能。
    if (custom.value) {
      body.modules = { ...modules.value }
      body.quotas = { ...quotas.value }
      body.policy = { ...policy.value }
    }

    const res = props.isReseller
      ? await reseller.reissueLicense(props.license.license_id, body)
      : await ls.reissueLicense(props.license.license_id, body)
    emit('done', { license_id: res.license_id })
    emit('close')
  } catch (e) {
    error.value = errText(e)
    // 代理商不給這顆——他們的申請走 `/reseller/requests`，那是另一支端點與另一份表單。
    canDraft.value = !props.isReseller && APPROVAL_CODES.includes(detailOf(e).code ?? '')
  } finally {
    busy.value = false
  }
}

/**
 * 批113：403 之後的出路——把畫面上這份內容改建成一筆**授權申請草稿**，並且**綁同一台主機**。
 *
 * `instance_id` 是這裡的重點：客戶頁那支草稿的主機下拉只列已連結的主機
 * （`deployment_links`），從那邊建的草稿常常沒有主機 → 核發時 `_activate` 找不到
 * `prior` → 舊證不會被 supersede，同一台機器上出現兩張各自有效的證。
 *
 * `modules`／`quotas`／`policy` **只有展開「自訂內容」時才帶**（與換發的送出規則同一條）：
 * 不帶＝核發時由伺服器端從套裝模板整包展開，那也是政策閘放行的前提。
 *
 * ★ 批114（#5538）：以前這裡**完全沒帶 `policy`**——操作者在畫面上取消的行為參數勾選，
 *   改建成草稿之後全部回到套裝預設值，而且畫面上看不出來。現在 typed body 與
 *   `requested` 兩邊都帶（後端只信 typed body，`requested` 是給人看的那一份）。
 *   全部取消＝送 `{}`（明確不帶任何行為參數），不是 `null`（那是「未指定」→ 補預設）。
 */
async function createDraft() {
  const lic = props.license
  if (!lic) return
  draftBusy.value = true
  const requested: Record<string, unknown> = {
    plan: plan.value,
    deployment_mode: lic.deployment_mode ?? 'online',
  }
  if (termTotal.value != null) {
    const [d, m] = splitMinutes(termTotal.value)
    requested.term_days = d
    requested.term_minutes = m
  }
  if (custom.value) {
    requested.modules = { ...modules.value }
    requested.quotas = { ...quotas.value }
    requested.policy = { ...policy.value }
  }
  try {
    await ls.createLicenseRequest({
      customer_id: lic.customer_id,
      // 形狀與 `CustomersView.draftRequestJson()` 一致——申請頁讀的就是 `requested`。
      request_json: {
        product: lic.product ?? null,
        instance_id: lic.host_instance_id ?? null,
        machine_fingerprint: lic.machine_fingerprint ?? null,
        source: 'admin_console',
        requested,
      },
      product: lic.product ?? null,
      plan: plan.value,
      ...(custom.value ? { policy: { ...policy.value } } : {}),
    })
  } catch (e) {
    error.value = errText(e, '建立草稿失敗')
    return
  } finally {
    draftBusy.value = false
  }
  canDraft.value = false
  emit('close')
  await dialogs.notify({
    title: '已改建授權草稿',
    message:
      '已在〈授權申請 → 草稿〉建立一筆，內容與這裡填的一樣、綁同一台主機。' +
      '到那裡核對後按「確認送出」送審；核發時目前那張證會自動標成「已被取代」。',
  })
}

/** 政策閘擋下來的三個碼——訊息要指路，而且可以「改建授權草稿」接下去。 */
const APPROVAL_CODES = ['activation_requires_approval', 'superadmin_required', 'role_required']

/** 批114：`quotas_invalid` 另外帶 `reason`／`key`／`label`（哪一個配額鍵、為什麼不合）。 */
function detailOf(e: unknown): {
  code?: string
  message?: string
  reason?: string
  key?: string
  label?: string
} {
  const d = e instanceof ApiError && e.payload ? (e.payload as { detail?: unknown }).detail : null
  return (d ?? {}) as { code?: string; message?: string; reason?: string; key?: string; label?: string }
}

function errText(e: unknown, fallback = '換發失敗'): string {
  const obj = detailOf(e)
  if (APPROVAL_CODES.includes(obj.code ?? '')) {
    // 後端的 message 已經印中文角色名（批110 起連 `policy_label` 也一起回）。
    // 代理商沒有「改建授權草稿」那顆鈕（他們走 `/reseller/requests`），指路的話要分開講。
    return (
      (obj.message ?? '這份內容需要更高身分才能直接核發。') +
      (props.isReseller
        ? '（要改成非標準內容請改走「授權申請」流程。）'
        : '（要改成非標準內容請改走「授權申請」流程——下面那顆按鈕會把這份內容原樣建成草稿、綁同一台主機。）')
    )
  }
  if (obj.code === 'policy_invalid') {
    return obj.message ?? '行為參數的值不合這個產品的憑證設計。'
  }
  // 批114：配額填不齊／不能設成無上限。後端回的是鍵名與中文 label——指名是哪一項，
  // 不要只丟一句「配額不正確」讓人逐欄猜。
  if (obj.code === 'quotas_invalid') {
    const name = obj.label || obj.key || '其中一項'
    return obj.reason === 'quota_not_nullable'
      ? `「${name}」不能設成「無上限」，請在下方〈自訂內容 → 額度〉填一個數字。`
      : `請在下方〈自訂內容 → 額度〉填寫「${name}」。`
  }
  if (obj.code === 'field_not_allowed') return obj.message ?? '這個欄位只能由我方指定。'
  if (obj.code === 'not_before_in_the_past') return obj.message ?? '啟用時間不能是過去的時刻。'
  if (obj.code === 'not_before_after_expiry') return obj.message ?? '啟用時間必須早於到期時刻。'
  if (obj.code === 'hosted_instance') return obj.message ?? '代管站的授權由中台派送，不走換發。'
  if (obj.code === 'expiry_in_the_past') return obj.message ?? '指定的到期時刻已經過了。'
  if (obj.code === 'base_has_no_host') return obj.message ?? '這張授權沒有綁定主機。'
  // 批155：內部套裝／模組、未知模組等跨畫面共用的碼。
  const known = knownErrorText(obj)
  if (known) return known
  return obj.message ?? fallback
}
</script>

<template>
  <UiModal :open="open" title="換發新證" width="560px" @close="emit('close')">
    <p class="hint">
      以這張授權綁定的<strong>機器</strong>為錨點重簽一張新的；目前那張會標成「已被取代」。
      沒有調整的欄位一律沿用原證，<strong>換套裝時模組與配額會整包套用新套裝的模板</strong>。
    </p>

    <div v-if="license?.status !== 'active'" class="hint sub">
      這張是已撤銷／已被取代的授權——換發只會拿它的機器資訊與內容當範本。
    </div>

    <div class="form">
      <label class="f">
        <span>套裝</span>
        <select v-model="plan">
          <option :value="null">客製（沿用原內容）</option>
          <option v-for="p in plans" :key="p.key" :value="p.key" :disabled="planLocked(p)">
            {{ p.label }}{{ isInternalPlan(p) ? '（內部）' : '' }}
          </option>
        </select>
      </label>

      <!-- 批114：兩個獨立的數字欄拔掉了，數量一律在下面〈自訂內容 → 額度〉裡填
           （欄位清單由該產品的憑證設計決定）。 -->

      <label class="f">
        <span>期限</span>
        <TermField v-model="termTotal" nullable />
      </label>

      <label class="f">
        <span>到期日</span>
        <select v-model="expiryMode">
          <option value="on_fetch">首次取證才起算（建議）</option>
          <option value="keep">沿用原到期日</option>
          <option value="at">指定時刻</option>
        </select>
      </label>

      <label v-if="expiryMode === 'at'" class="f">
        <span>到期時刻</span>
        <input v-model="expiresAt" type="datetime-local" />
      </label>

      <label v-if="!isReseller" class="f">
        <span>啟用時間</span>
        <input v-model="notBefore" type="datetime-local" />
      </label>
      <!-- 批113：原證有「延後生效」時一定要講。留空＝**沿用原證那個時刻**（後端
           `_carry_over` 會帶過去），畫面上不預填就等於操作者完全看不到自己簽了一張
           「到某天才生效」的證。預填輸入框會反過來改變語意（變成顯式指定），所以只提示。 -->
      <div v-if="!isReseller && baseNotBefore" class="f nb-carry">
        <span></span>
        <small>沿用原證：{{ baseNotBefore }}（清空另填可改）</small>
      </div>

      <label class="f">
        <span>備註</span>
        <input v-model="note" placeholder="例：客戶付款後恢復（只進稽核）" />
      </label>
    </div>

    <p v-if="notBefore" class="hint sub">
      這張證在指定時刻之前不生效；期限從那一刻起算（到期日簽發當下就定好）。
      站台**要等到那一刻才會換上它**，在那之前繼續用現在這張。
    </p>

    <!-- 批110：自訂內容（模組／額度／行為參數）。代理商 realm 不顯示。 -->
    <div v-if="!isReseller" class="custom">
      <button type="button" class="custom-toggle" @click="toggleCustom">
        {{ custom ? '▾' : '▸' }} 自訂內容（模組／額度／行為參數）
      </button>
      <small class="custom-note">
        改成非標準內容需「營運主管」以上；沒展開就是沿用原證。
      </small>
      <!-- 批113：換了套裝時三個編輯器裡放的是**新套裝的模板值**（`watch(plan)` 帶入的），
           不講清楚會被當成「系統把客戶的額度改掉了」。 -->
      <p v-if="seededHint" class="seeded-hint">{{ seededHint }}</p>
      <div v-if="custom" :key="customKey" class="custom-body">
        <div class="custom-sec">
          <div class="custom-title">模組</div>
          <ModulePicker v-model="modules" :spec="spec" :allow-internal="allowInternal" />
        </div>
        <div class="custom-sec">
          <div class="custom-title">額度</div>
          <!-- 批114：必填與「可不可以無上限」由憑證設計宣告，編輯器自己畫紅字並回報
               `update:valid`；沒填齊就不讓送出（後端那一關是 422 `quotas_invalid`）。 -->
          <QuotaEditor v-model="quotas" :spec="spec" @update:valid="quotaValid = $event" />
        </div>
        <div v-if="(spec?.policies?.length ?? 0) > 0" class="custom-sec">
          <div class="custom-title">行為參數</div>
          <PolicyEditor v-model="policy" :spec="spec" />
        </div>
      </div>
    </div>

    <p v-if="shortWarning" class="warn-text">⚠️ {{ shortWarning }}</p>
    <!-- 批114：配額有必填鍵沒填（編輯器已在該列畫紅字）——按鈕反灰的理由要寫出來，
         不然只會看到「按了沒反應」。 -->
    <p v-if="quotaBlocked" class="warn-text">
      〈自訂內容 → 額度〉還有必填的項目沒填，填完才能送出。
    </p>
    <p v-if="error" class="warn-text">{{ error }}</p>

    <!-- 批113：403 之後的出路。換發沒有「降級成申請」的路，而客戶頁那支草稿選不到
         未連結的主機——沒有這顆，操作者只能開一張綁不到機器的草稿，核發後舊證還活著。 -->
    <div v-if="canDraft" class="draft-cta">
      <UiButton variant="secondary" size="sm" :loading="draftBusy" @click="createDraft">
        改建授權草稿
      </UiButton>
      <small>
        內容與這裡填的一樣、綁同一台主機（{{ license?.host_instance_id?.slice(0, 8) || '未綁主機' }}）；
        核發時目前那張證會自動標成「已被取代」。
      </small>
    </div>

    <!-- ★ slot 名稱是 `foot` 不是 `footer`（`UiModal.vue:50`）——寫錯不會報錯，
         整排按鈕會**靜默消失**，只有實際開畫面才看得出來。 -->
    <template #foot>
      <UiButton @click="emit('close')">取消</UiButton>
      <UiButton variant="primary" :loading="busy" :disabled="quotaBlocked" @click="submit">
        換發
      </UiButton>
    </template>
  </UiModal>
</template>

<style scoped>
.form {
  display: grid;
  gap: 10px;
  margin-top: 12px;
}
.f {
  display: grid;
  grid-template-columns: 7em 1fr;
  gap: 10px;
  align-items: center;
}
/* 批97 的全域修正：`.field > input{width:100%;min-width:0}`，這裡沿用同一條 */
.f > input,
.f > select {
  width: 100%;
  min-width: 0;
}
.hint.sub {
  margin-top: 6px;
  color: var(--fg-3);
}
.warn-text {
  margin-top: 10px;
  color: var(--warning);
}
/* 批113：跟著「啟用時間」那一列縮排（左欄留白、右欄放提示），不另起一段。 */
.nb-carry > small {
  color: var(--fg-3);
  font-size: var(--fs-11);
}
.draft-cta {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 8px;
}
.draft-cta > small {
  flex: 1 1 260px;
  color: var(--fg-3);
  font-size: var(--fs-11);
}
.seeded-hint {
  margin-top: 8px;
  color: var(--fg-3);
  font-size: var(--fs-11);
}
.custom {
  margin-top: 12px;
  border-top: 1px solid var(--border-2);
  padding-top: 10px;
}
.custom-toggle {
  border: none;
  background: none;
  padding: 0;
  color: var(--fg-2);
  font: inherit;
  cursor: pointer;
}
.custom-toggle:hover {
  color: var(--fg);
}
.custom-note {
  display: block;
  margin-top: 2px;
  color: var(--fg-4);
  font-size: var(--fs-11);
}
.custom-body {
  display: grid;
  gap: 12px;
  margin-top: 10px;
}
.custom-title {
  margin-bottom: 4px;
  font-size: var(--fs-12);
  font-weight: 600;
  color: var(--fg-2);
}
</style>
